import React, { useState } from 'react';
import { Box, Chip, FormControlLabel, IconButton, Switch, TableCell, TableRow, Typography } from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon
} from '@mui/icons-material';

/**
 * "Group by client" support for list tables (Proposals, Contracts).
 * The toggle is remembered per browser; collapsed groups are not.
 */

const readStoredFlag = (key) => {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

export const useClientGrouping = (storageKey) => {
  const [grouped, setGroupedState] = useState(() => readStoredFlag(storageKey));
  const [collapsed, setCollapsed] = useState(() => new Set());

  const setGrouped = (value) => {
    setGroupedState(value);
    try {
      window.localStorage.setItem(storageKey, String(value));
    } catch {
      // Storage unavailable: the toggle just won't be remembered
    }
  };

  const toggleCollapsed = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return { grouped, setGrouped, collapsed, toggleCollapsed };
};

// Groups items by client, ordered by client name. Items keep their
// incoming order inside each group, so any table sort still applies.
export const groupByClient = (items, getClientId, getClientName) => {
  const groups = new Map();
  (items || []).forEach((item) => {
    const key = getClientId(item) || '';
    if (!groups.has(key)) {
      groups.set(key, { key, name: (key && getClientName(key)) || 'No client', items: [] });
    }
    groups.get(key).items.push(item);
  });
  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
};

export const GroupByClientToggle = ({ grouped, onChange }) => (
  <FormControlLabel
    control={<Switch checked={grouped} onChange={(e) => onChange(e.target.checked)} />}
    label="Group by client"
    sx={{ mr: 1 }}
  />
);

export const ClientGroupHeaderRow = ({ group, colSpan, collapsed, onToggle, total }) => (
  <TableRow
    onClick={onToggle}
    sx={{ cursor: 'pointer', bgcolor: 'action.hover', '& > td': { borderBottomWidth: 2 } }}
  >
    <TableCell colSpan={colSpan} sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" aria-label={collapsed ? 'Expand group' : 'Collapse group'}>
          {collapsed ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle2" fontWeight="bold">{group.name}</Typography>
        <Chip size="small" label={group.items.length} />
        {total != null && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {total}
          </Typography>
        )}
      </Box>
    </TableCell>
  </TableRow>
);
